import React, { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import SelectList from "../../../shared/inputs/SelectList";
import AtoSignInButton from "./AtoSignInButton";
import TakeOffSourceHeader from "../shared/TakeOffSourceHeader";
import AutocompleteField from "../../../shared/inputs/AutocompleteField";
import { useConnectionTransition } from "../../../hooks/useConnectionTransition";
import { useSignOutReset } from "../../../hooks/useSignOutReset";
import { useDrawerOpenReset } from "../../../hooks/useDrawerOpenReset";
import { useAto } from "./AtoContext";
import { fetchAtoHubs, fetchAtoPackages, fetchAtoProjects } from "../../../../integrations/qto/sources/ato/api";
import type { AtoAccProject, AtoHub, AtoPackage } from "../../../../integrations/qto/sources/ato/types";
import { useBanner } from "../../../context/BannerContext";
import { workbookCache } from "../../../../excel/workbook/cache";
import {
  ATO_HUBS_LOAD_ERROR,
  ATO_HUB_LABEL,
  ATO_NOT_ENABLED,
  ATO_NO_TAKEOFFS,
  ATO_PACKAGES_EMPTY,
  ATO_PROJECTS_LOAD_ERROR,
  TAKEOFF_PACKAGE_HEADING,
} from "../../../constants";
import { ediphiPalette } from "../../../styles/colors";

const ATOList: React.FC = () => {
  const {
    atoState,
    atoHub,
    atoProject,
    atoPackage,
    isAtoAuthenticated,
    connectAtoFromSelection,
    selectorPrefillHub,
    clearSelectorPrefill,
  } = useAto();
  const { showBanner } = useBanner();

  const [hub, setHub] = useState<AtoHub | null>(null);
  const [project, setProject] = useState<AtoAccProject | null>(null);
  const [hubs, setHubs] = useState<AtoHub[]>([]);
  const [projects, setProjects] = useState<AtoAccProject[]>([]);
  const [packages, setPackages] = useState<AtoPackage[]>([]);
  const [loadingHubs, setLoadingHubs] = useState<boolean>(true);
  const [loadingProjects, setLoadingProjects] = useState<boolean>(false);
  const [loadingPackages, setLoadingPackages] = useState<boolean>(false);
  const [hubsLoaded, setHubsLoaded] = useState<boolean>(false);
  const [projectsLoaded, setProjectsLoaded] = useState<boolean>(false);
  const [hubPrefillDone, setHubPrefillDone] = useState<boolean>(false);
  const [projectPrefillDone, setProjectPrefillDone] = useState<boolean>(false);

  useConnectionTransition("ato", atoState, () => {
    setHub(null);
    setProject(null);
  });

  // Shared by both triggers so a reopen and a sign-out cannot reset different amounts.
  const resetSelection = useCallback(() => {
    setHubPrefillDone(false);
    setProjectPrefillDone(false);
    setHub(null);
    setProject(null);
  }, []);

  useDrawerOpenReset("takeOff", resetSelection);
  useSignOutReset(isAtoAuthenticated, resetSelection);

  useEffect(() => {
    if (!isAtoAuthenticated) return;

    let isCurrent = true;
    const load = async () => {
      setLoadingHubs(true);
      const res = await fetchAtoHubs();
      if (!isCurrent) return;
      if (res.success) {
        setHubs(res.data ?? []);
      } else {
        setHubs([]);
        showBanner(ATO_HUBS_LOAD_ERROR, "error");
      }
      setHubsLoaded(true);
      setLoadingHubs(false);
    };

    void load();
    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAtoAuthenticated]);

  // Once per drawer open, not whenever `hub` happens to be empty — the field is clearable, and
  // re-filling it on empty would make it impossible to clear. The whole cascade below (prefill →
  // connected → cached → sole hub) is initialisation, so it belongs behind the same one shot.
  //
  // `hubPrefillDone` is set per-branch rather than once up front, and that placement is load
  // bearing: it is in this effect's deps, so setting it early re-renders, runs the cleanup below,
  // and cancels the cache read before it resolves. Leaving it false across the await is what lets
  // the cached-hub and sole-hub branches actually apply.
  useEffect(() => {
    if (!hubsLoaded || hubPrefillDone) return;
    if (selectorPrefillHub) {
      setHubPrefillDone(true);
      setHub(selectorPrefillHub);
      clearSelectorPrefill();
      return;
    }
    if (atoHub) {
      setHubPrefillDone(true);
      setHub(atoHub);
      return;
    }
    if (hubs.length === 0) {
      setHubPrefillDone(true);
      return;
    }

    let isCurrent = true;
    void (async () => {
      const cachedHubId = await workbookCache.get("atoHubId");
      if (!isCurrent) return;
      setHubPrefillDone(true);
      if (typeof cachedHubId === "string" && cachedHubId) {
        const match = hubs.find((h) => h.id === cachedHubId);
        if (match) {
          setHub(match);
          return;
        }
      }
      if (hubs.length === 1) setHub(hubs[0]);
    })();
    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubsLoaded, hubPrefillDone, hubs, selectorPrefillHub, atoHub]);

  useEffect(() => {
    setProjectsLoaded(false);
    if (!hub) {
      setProjects([]);
      return;
    }

    let isCurrent = true;
    const load = async () => {
      setLoadingProjects(true);
      const res = await fetchAtoProjects(hub.id);
      if (!isCurrent) return;
      if (res.success) {
        setProjects(res.data ?? []);
      } else {
        showBanner(ATO_PROJECTS_LOAD_ERROR, "error");
        setProjects([]);
      }
      setProjectsLoaded(true);
      setLoadingProjects(false);
    };

    void load();
    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hub]);

  // Gated on `projectsLoaded` rather than on `loadingProjects` being false: that flag starts out
  // false, so this would otherwise fire in the commit where `hub` was just set but before the
  // loader's `setLoadingProjects(true)` had re-rendered — spending the one shot on the previous
  // hub's list. Only fires for the first hub of an open, so switching hubs never re-selects.
  useEffect(() => {
    if (!projectsLoaded || projectPrefillDone) return;
    setProjectPrefillDone(true);
    if (atoProject && projects.some((p) => p.id === atoProject.id)) setProject(atoProject);
  }, [projectsLoaded, projectPrefillDone, projects, atoProject]);

  useEffect(() => {
    if (!project) {
      setPackages([]);
      return;
    }

    let isCurrent = true;
    const load = async () => {
      setLoadingPackages(true);
      const res = await fetchAtoPackages(project.id);
      if (!isCurrent) return;
      if (res.success) {
        const rows = res.data ?? [];
        setPackages(rows);
        if (rows.length === 0) {
          showBanner(ATO_NO_TAKEOFFS, "info");
        }
      } else {
        showBanner(ATO_NOT_ENABLED, "error");
        setPackages([]);
      }
      setLoadingPackages(false);
    };

    void load();
    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  const handleHubChange = (nextHub: AtoHub | null) => {
    setHub(nextHub);
    setProject(null);
  };

  const handleSelectPackage = async (pkg: AtoPackage) => {
    if (!hub || !project) return;
    // No close here: useConnectionTransition closes on the connected transition, so a failed connect
    // leaves the drawer open on this list for an immediate retry.
    await connectAtoFromSelection(hub, project, pkg);
  };

  if (!isAtoAuthenticated) return <AtoSignInButton />;

  return (
    <Box>
      <Box sx={{ p: 2 }}>
        <Box sx={{ mb: "16px" }}>
          <TakeOffSourceHeader type="ato" />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <AutocompleteField<AtoHub>
            label={ATO_HUB_LABEL}
            options={hubs}
            value={hub}
            onChange={handleHubChange}
            getOptionLabel={(option) => option.name || option.id}
            getOptionId={(option) => option.id}
            loading={loadingHubs}
          />
          <AutocompleteField<AtoAccProject>
            label="Project"
            options={projects}
            value={project}
            onChange={setProject}
            getOptionLabel={(option) => option.name || option.id}
            getOptionId={(option) => option.id}
            disabled={!hub}
            loading={loadingProjects}
          />
        </Box>
      </Box>
      {project && (
        <Box
          sx={{
            p: 2,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            borderTop: `1px solid ${ediphiPalette.borderDivider}`,
          }}
        >
          <SelectList<AtoPackage>
            title={TAKEOFF_PACKAGE_HEADING}
            items={packages.map((pkg) => ({ id: pkg.id, label: pkg.name || pkg.id, data: pkg }))}
            onSelectItem={(pkg) => void handleSelectPackage(pkg)}
            selectedItemId={atoPackage?.id ?? null}
            // Covers connecting as well as fetching: the rows are the only click target, and the
            // drawer now stays open while the connect runs.
            loading={loadingPackages || atoState === "loading"}
            emptyMessage={ATO_PACKAGES_EMPTY}
          />
        </Box>
      )}
    </Box>
  );
};

export default ATOList;
